import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';

export type ToastTone = 'success' | 'error' | 'warn' | 'info';

export type Toast = {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  duration?: number;
};

type ToastInput = Omit<Toast, 'id' | 'tone'> & { tone?: ToastTone; id?: string };

type ToastContextValue = {
  show: (input: ToastInput) => string;
  success: (title: string, description?: string) => string;
  error: (title: string, description?: string) => string;
  warn: (title: string, description?: string) => string;
  info: (title: string, description?: string) => string;
  dismiss: (id: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const ICONS: Record<ToastTone, ReactNode> = {
  success: <CheckCircle2 size={18} />,
  error: <XCircle size={18} />,
  warn: <AlertTriangle size={18} />,
  info: <Info size={18} />,
};

let counter = 0;
function makeId() {
  counter += 1;
  return `t-${Date.now().toString(36)}-${counter}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, number>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (input: ToastInput): string => {
      const id = input.id ?? makeId();
      const tone: ToastTone = input.tone ?? 'info';
      const duration = input.duration ?? (tone === 'error' ? 5500 : 3800);
      setToasts((cur) => {
        const next = [...cur.filter((t) => t.id !== id), { id, tone, title: input.title, description: input.description, duration }];
        return next.slice(-5);
      });
      if (duration > 0) {
        const t = window.setTimeout(() => dismiss(id), duration);
        timersRef.current.set(id, t);
      }
      return id;
    },
    [dismiss],
  );

  useEffect(
    () => () => {
      timersRef.current.forEach((id) => window.clearTimeout(id));
      timersRef.current.clear();
    },
    [],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      success: (title, description) => show({ tone: 'success', title, description }),
      error: (title, description) => show({ tone: 'error', title, description }),
      warn: (title, description) => show({ tone: 'warn', title, description }),
      info: (title, description) => show({ tone: 'info', title, description }),
      dismiss,
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined'
        ? createPortal(
            <div className="toaster" role="region" aria-live="polite" aria-label="Notifications">
              {toasts.map((t) => (
                <div key={t.id} className={`toast toast-${t.tone}`} role="status">
                  <span className="toast-icon" aria-hidden>
                    {ICONS[t.tone]}
                  </span>
                  <div className="toast-text">
                    <div className="toast-title">{t.title}</div>
                    {t.description ? <div className="toast-desc">{t.description}</div> : null}
                  </div>
                  <button type="button" className="toast-close" aria-label="Dismiss" onClick={() => dismiss(t.id)}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
